import React from 'react';
import { AlertTriangle, ArrowUpRight, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const API_URL = '';

export default function LimitWarningDialog({ 
  open, 
  onClose, 
  limitInfo,
  onUpgrade 
}) {
  if (!limitInfo) return null;
  
  const {
    resource_name = 'Resource',
    current,
    limit,
    tier,
    message,
    upgrade_available,
    upgrade_info
  } = limitInfo;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center gap-3 text-red-600">
            <div className="p-2 bg-red-100 rounded-full">
              <AlertTriangle className="w-6 h-6" />
            </div>
            <DialogTitle className="text-xl text-red-900">
              Limit Reached
            </DialogTitle>
          </div>
          <DialogDescription className="pt-4 text-base text-slate-700">
            {message || `You've reached your ${tier} limit for ${resource_name.toLowerCase()}.`}
          </DialogDescription>
        </DialogHeader>
        
        <div className="py-4">
          {/* Current Usage */}
          <div className="bg-red-50 rounded-lg p-4 mb-4">
            <div className="flex items-center justify-between">
              <span className="text-slate-600">{resource_name}</span>
              <span className="font-bold text-red-700">
                {current} / {limit}
              </span>
            </div>
            <div className="w-full bg-red-200 rounded-full h-2 mt-2">
              <div className="bg-red-600 h-2 rounded-full w-full" />
            </div>
          </div>
          
          {/* Upgrade Option */}
          {upgrade_available && upgrade_info && (
            <div className="bg-gradient-to-r from-indigo-50 to-violet-50 rounded-lg p-4 border border-indigo-200">
              <div className="flex items-center gap-2 mb-2">
                <ArrowUpRight className="w-5 h-5 text-indigo-600" />
                <span className="font-medium text-indigo-900">
                  Upgrade to {upgrade_info.next_tier?.replace('tier', 'Tier ')}
                </span>
              </div>
              <p className="text-sm text-indigo-700 mb-3">
                Increase your limit from <strong>{limit}</strong> to <strong>{upgrade_info.new_limit || 'Unlimited'}</strong>
                {upgrade_info.fee_reduction && ` and ${upgrade_info.fee_reduction}`}
              </p>
              <Button 
                className="w-full bg-gradient-to-r from-indigo-500 to-violet-500 hover:from-indigo-600 hover:to-violet-600"
                onClick={onUpgrade}
              >
                View Upgrade Options
              </Button>
            </div>
          )}
          
          {/* Alternative Actions */}
          <div className="mt-4 text-sm text-slate-500">
            <p>You can also:</p>
            <ul className="list-disc list-inside mt-1 space-y-1">
              <li>Remove unused {resource_name.toLowerCase()} to free up space</li>
              <li>Contact support for assistance</li>
            </ul>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onClose(false)}>
            <X className="w-4 h-4 mr-2" />
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Hook to handle limit errors
export function useLimitCheck() {
  const [limitDialogOpen, setLimitDialogOpen] = React.useState(false);
  const [limitInfo, setLimitInfo] = React.useState(null);
  
  const handleApiError = (error) => {
    // Check if error is a limit block response
    if (error?.response?.status === 403) {
      const detail = error.response.data?.detail;
      if (detail && detail.blocked) {
        setLimitInfo(detail);
        setLimitDialogOpen(true);
        return true; // Indicates limit error was handled
      }
    }
    return false; // Error was not a limit error
  };
  
  const checkLimit = async (resourceType, token) => {
    try {
      const response = await fetch(
        `${API_URL}/api/usage/c03c57eb-ef2d-4eaa-99ce-78870f483efb/limit-status/${resourceType}`,
        {
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.at_limit) {
          setLimitInfo({
            resource_name: data.name,
            current: data.current,
            limit: data.limit,
            tier: 'your current plan',
            message: `You've reached your limit of ${data.limit} ${data.name.toLowerCase()}.`,
            upgrade_available: data.upgrade_available,
            upgrade_info: data.upgrade_info
          });
          setLimitDialogOpen(true);
          return false; // Cannot proceed
        }
        return true; // Can proceed
      }
    } catch (err) {
      console.error('Error checking limit:', err);
    }
    return true; // Allow by default if check fails
  };
  
  return {
    limitDialogOpen,
    setLimitDialogOpen,
    limitInfo,
    handleApiError,
    checkLimit,
    LimitWarningDialog: () => (
      <LimitWarningDialog
        open={limitDialogOpen}
        onClose={setLimitDialogOpen}
        limitInfo={limitInfo}
        onUpgrade={() => {
          setLimitDialogOpen(false);
          window.location.href = '/admin/portal-settings';
        }}
      />
    )
  };
}
